import { Activity, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useHrvTrends, HrvTrendPoint } from '@/hooks/use-health-trends';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';

interface HrvTrendChartProps {
  clientId: string;
  days?: number;
  className?: string;
}

function formatDate(dateStr: string) {
  try {
    return format(parseISO(dateStr), 'MMM d');
  } catch {
    return dateStr;
  }
}

function getHrvStatus(hrv: number, avg: number): { label: string; color: string } {
  const ratio = hrv / avg;
  if (ratio >= 1.1) return { label: 'Excellent', color: 'text-success' };
  if (ratio >= 0.95) return { label: 'Good', color: 'text-info' };
  if (ratio >= 0.85) return { label: 'Fair', color: 'text-warning' };
  return { label: 'Low', color: 'text-destructive' };
}

function calculateTrend(data: HrvTrendPoint[]): 'improving' | 'declining' | 'stable' {
  if (data.length < 7) return 'stable';

  const midpoint = Math.floor(data.length / 2);
  const firstHalf = data.slice(0, midpoint);
  const secondHalf = data.slice(midpoint);

  const firstAvg = firstHalf.reduce((sum, d) => sum + (d.value || 0), 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((sum, d) => sum + (d.value || 0), 0) / secondHalf.length;

  if (secondAvg > firstAvg * 1.05) return 'improving';
  if (secondAvg < firstAvg * 0.95) return 'declining';
  return 'stable';
}

export function HrvTrendChart({ clientId, days = 30, className }: HrvTrendChartProps) {
  const { data, loading, error } = useHrvTrends(clientId, days);

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="chart-height-sm w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-success" />
            HRV Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center chart-height-sm text-muted-foreground">
            <Activity className="h-12 w-12 mb-2 opacity-20" />
            <p className="text-sm">No HRV data available</p>
            <p className="text-xs">Connect a health device to track HRV</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const trend = calculateTrend(data);
  const avgHrv = data.reduce((sum, d) => sum + (d.value || 0), 0) / data.length;
  const latestHrv = data[data.length - 1]?.value || 0;
  const hrvStatus = getHrvStatus(latestHrv, avgHrv);

  // Transform data for chart
  const chartData = data.map((d) => ({
    date: formatDate(d.date),
    hrv: d.value,
    avg: d.rolling_avg,
  }));

  // Calculate min/max for domain with padding
  const minHrv = Math.min(...data.map((d) => d.value));
  const maxHrv = Math.max(...data.map((d) => d.value));
  const padding = (maxHrv - minHrv) * 0.1;

  return (
    <Card className={cn('border', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-success" />
            HRV Trends
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {trend === 'improving' && <TrendingUp className="h-4 w-4 text-success" />}
              {trend === 'declining' && <TrendingDown className="h-4 w-4 text-destructive" />}
              {trend === 'stable' && <Minus className="h-4 w-4 text-muted-foreground" />}
              <span className="capitalize">{trend}</span>
            </div>
            <div className="text-right">
              <span className={cn('text-xl font-bold', hrvStatus.color)}>
                {latestHrv}ms
              </span>
              <span className={cn('text-xs block', hrvStatus.color)}>{hrvStatus.label}</span>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div role="img" aria-label="Heart rate variability trend over time">
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              domain={[
                Math.max(0, Math.floor(minHrv - padding)),
                Math.ceil(maxHrv + padding),
              ]}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value: number, name: string) => [
                `${value}ms`,
                name === 'hrv' ? 'HRV' : '7-day Avg',
              ]}
            />
            <ReferenceLine
              y={avgHrv}
              stroke="#94a3b8"
              strokeDasharray="5 5"
              label={{ value: 'Avg', fontSize: 10, fill: '#94a3b8' }}
            />
            <Line
              type="monotone"
              dataKey="avg"
              stroke="#94a3b8"
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
              name="avg"
            />
            <Line
              type="monotone"
              dataKey="hrv"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: '#10b981', strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, fill: '#10b981' }}
              name="hrv"
            />
          </LineChart>
        </ResponsiveContainer>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-2 mt-4 text-center text-xs">
          <div className="bg-muted/50 rounded p-2">
            <div className="text-muted-foreground">Current</div>
            <div className={cn('font-semibold', hrvStatus.color)}>{latestHrv}ms</div>
          </div>
          <div className="bg-success/10 rounded p-2">
            <div className="text-muted-foreground">Average</div>
            <div className="font-semibold text-success">{Math.round(avgHrv)}ms</div>
          </div>
          <div className="bg-info/10 rounded p-2">
            <div className="text-muted-foreground">Max</div>
            <div className="font-semibold text-info">{maxHrv}ms</div>
          </div>
          <div className="bg-warning/10 rounded p-2">
            <div className="text-muted-foreground">Min</div>
            <div className="font-semibold text-warning">{minHrv}ms</div>
          </div>
        </div>

        {/* Low HRV warning */}
        {latestHrv < avgHrv * 0.85 && (
          <div className="flex items-center gap-2 mt-3 p-2 bg-warning/10 text-warning rounded text-xs">
            <AlertTriangle className="h-4 w-4" />
            <span>HRV is below normal. Consider recommending rest or light activity.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
